// Имена событий аналитики — чистая логика без window (тесты). Экран — из маршрута, действие — из адреса пишущего
// запроса: адреса даёт сервер, поэтому списка действий у клиента нет — новое действие бэкенда появится в отчётах само.
// Ограничения Google Analytics 4: имя события — до 40 знаков, латиница/цифры/подчёркивание, с буквы; не больше 500 имён.

const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/** Вкладка нижней панели (серверный ключ) → имя корневого экрана по словарю v1 (§4.1). Незнакомый ключ — сам ключ. */
const TAB_SCREENS: Record<string, string> = {
  matches: 'match_center', leagues: 'leagues', favorites: 'favorites', news: 'news_hub', highlights: 'highlights',
  home: 'home', fantasy: 'game_world', game: 'game',
};
export const tabScreen = (key: string) => TAB_SCREENS[key] ?? (clean(key).slice(0, 40) || 'home');

/** Экраны приложения по словарю v1 (§4.1): первый сегмент адреса → имя. Прочие — по сегментам адреса без номеров. */
const SCREENS: Record<string, string> = {
  match: 'match_detail', league: 'league_detail', team: 'team_detail', player: 'player_detail', venue: 'venue_detail',
  news: 'article_detail', feed: 'news_thematic', videos: 'highlights', language: 'language_settings',
  premium: 'paywall',
};
/** Вложенные экраны с именем словаря (§5.1): «Уведомления» в настройках (HQ-201) — notification_settings. */
const NESTED: Record<string, string> = { 'settings/push': 'notification_settings' };

/** Маршрут → имя экрана словаря v1: «/match/123» → match_detail, «/tab/matches» → match_center, «/world/player/store» →
 *  world_store. Номера и коды (сегмент с цифрой) в имя не идут — номер едет параметром content_id. */
export function screenName(pathname: string, search = ''): string {
  const parts = pathname.split('?')[0]!.split('/').filter(Boolean);
  // папка сцены мира: /world/player/hub?hub=shop&tab=store — экран это папка и её вкладка, а не слово «hub»
  if (parts[0] === 'world' && parts[2] === 'hub') {
    const q = new URLSearchParams(search);
    const inner = [q.get('hub'), q.get('tab')].filter(Boolean).join('_');
    return inner ? clean(`world_${inner}`).slice(0, 40) : 'world_hub';
  }
  if (parts.length === 0) return 'home';
  // мир: вкладка раздела (player) — не экран; экран — папка поверх сцены
  if (parts[0] === 'world') return clean(parts[2] ? `world_${parts[2]}` : `world_${parts[1] ?? 'scene'}`) || 'world';
  if (parts[0] === 'tab') return tabScreen(parts[1] ?? '');
  if (parts[0] === 'game' && parts[1] === 'join') return 'game_join';
  const nested = NESTED[parts.slice(0, 2).join('/')];
  if (nested && parts.length === 2) return nested;
  if (parts.length <= 2 && SCREENS[parts[0]!]) return SCREENS[parts[0]!]!;
  return clean(parts.filter((p) => !/\d/.test(p)).join('_')).slice(0, 40) || 'home';
}

/** Номер сущности из адреса экрана («/match/71314» → "71314") — параметр content_id. */
export const screenContentId = (pathname: string) => /\/(\d{1,12})(?:[/?]|$)/.exec(pathname)?.[1];

/** Состояние матча по словарю v1: идущий — только по is_live сервера; NS — не начался; Finished — сыгран; прочее — other. */
export type MatchState = 'live' | 'upcoming' | 'finished' | 'other';
export function matchState(m: { is_live?: boolean | null; status?: string | null }): MatchState {
  if (m.is_live === true) return 'live';
  if (m.status === 'NS') return 'upcoming';
  if (m.status === 'Finished') return 'finished';
  return 'other';
}

type MatchLike = { id: number; is_live?: boolean | null; status?: string | null; league?: { id: number } | null };
/** Параметры матча в content_open: состояние и лига (номер строкой; нет лиги — без параметра). */
export const matchContent = (m: MatchLike) => ({ match_state: matchState(m), league_id: m.league?.id != null ? String(m.league.id) : undefined });

/** match_detail_tab_select: только смена вкладки человеком — нажатие на открытую вкладку не событие (null). */
export function matchTabSelect(key: string, active: string, m: MatchLike) {
  return key === active ? null : { tab: key, fixture_id: String(m.id), match_state: matchState(m) };
}

/** События чата матча (HQ-2336): match_chat_open, match_chat_send {reply: 0 | 1}, match_chat_reply_open, match_chat_report,
 *  match_chat_hide_author, match_chat_rules_accept — у всех fixture_id номером матча строкой (как match_detail_tab_select). */
export function matchChatParams(fixtureId: string | number, reply?: boolean) {
  return reply === undefined ? { fixture_id: String(fixtureId) } : { fixture_id: String(fixtureId), reply: reply ? 1 : 0 };
}

/** match_center_date_change (HQ-608/609): смена дня матч-центра человеком — вкладкой дней (tab) или в календаре (calendar).
 *  date_offset — дни от сегодня (вчера −1, завтра +1); тот же день — не событие (null). Даты — YYYY-MM-DD полотна. */
export function matchDateChange(date: string, active: string, today: string, source: 'tab' | 'calendar') {
  if (date === active) return null;
  const day = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / 86_400_000;
  return { date_offset: Math.round(day(date) - day(today)), source, date };
}

/** Сегодня по часам устройства (YYYY-MM-DD) — когда у полотна нет дня с is_today. */
export function localToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Что не считаем действием: вход и обновление сессии (служебное), отчёты боя раз в 5 с (отдельные события play/level),
 *  события обучения мира (HQ-190: их пишут свои tutorial_step / tutorial_tip / tutorial_replay с ключом шага);
 *  избранное, прогноз и пачка карточек спонсоров — у них свои события словаря (favorite_add / favorite_remove,
 *  prediction_submit, sponsored_*), a_account_favorites* и a_predictions_* словарь запрещает; живой счёт в чате бота —
 *  своё bot_live_follow {action} (HQ-845); ответ квиза дня — своё quiz_answer {kind, correct} (HQ-2329). */
const SKIP = /\/account\/(guest|telegram|refresh)\/|\/live\/batch\/|\/world\/tutorial\/(events|replay)\/|\/account\/favorites\/|\/game\/[a-z]+\/predictions\/|\/sponsored\/events\/|\/account\/push\/bot-live-score\/|\/quiz\/[a-z]+\/today\/answer\//;
export const isBattleReport = (path: string) => /\/world\/battle\/report\/?(\?|$)/.test(path);

/** Адрес пишущего запроса → имя события: «…/game/cricket/world/battle/team/123/upgrade/» → a_world_battle_team_upgrade.
 *  null — не действие человека. Префикс a_ отделяет действия от штатных событий Google. */
export function actionName(method: string, path: string): `a_${string}` | null {
  if (method === 'GET' || SKIP.test(path) || isBattleReport(path)) return null;
  const tail = path.split('?')[0]!.replace(/^https?:\/\/[^/]+/, '').replace(/^\/?api\/v\d+\/mobile\//, '').replace(/^game\/[a-z]+\//, '');
  const words = tail.split('/').filter((p) => p && !/^\d+$/.test(p) && !/^[0-9a-f-]{16,}$/i.test(p)).map(clean).filter(Boolean);
  if (words.length === 0) return null;
  // DELETE/PUT/PATCH на тот же адрес — другое действие (снять избранное, изменить прогноз)
  const verb = method === 'POST' ? '' : `_${method.toLowerCase()}`;
  return `${`a_${words.join('_')}`.slice(0, 40 - verb.length).replace(/_+$/, '')}${verb}` as `a_${string}`;
}

/** Счёт товара витрины «…/world/store/<раздел>/<ключ>/invoice/» → «…/world/store/<раздел>/invoice/». */
const STORE_ITEM_INVOICE = /(\/world\/store\/[^/?]+)\/[^/?]+(\/invoice\/?)(?=\?|$)/;

/** Имя события действия (a_*): как actionName, но счёт витрины — без ключа товара (HQ-1045): gems/small и gems/gems-1 →
 *  одно a_world_store_gems_invoice, иначе у каждого пакета своё имя и воронку Stars не собрать. Какой пакет — в
 *  stars_invoice.item и item_click.item (там actionName с ключом, как был); адрес без ключа (store/vip/invoice/) — как есть. */
export const actionEventName = (method: string, path: string) => actionName(method, path.replace(STORE_ITEM_INVOICE, '$1$2'));

/** Исход действия одним словом: ok, либо машинная причина отказа (not_enough_gems — это спрос), либо код/статус. */
export function actionResult(status: number, code: string | null, reason: unknown): string {
  if (status >= 200 && status < 300) return 'ok';
  if (typeof reason === 'string' && reason) return clean(reason).slice(0, 40);
  return code ? clean(code).slice(0, 40) : status ? `http_${status}` : 'network';
}

/** Источник запуска из start_param: «match_123» → match, «ref_abc» → ref; пусто — direct. */
export const launchSource = (start: string | undefined) => (start ? clean(start.split(/[_-]/)[0] ?? '').slice(0, 24) || 'other' : 'direct');

// ---- Рекламная метка (вторая часть start_param после «__», см. deeplink.splitStartParam) ----
export interface Campaign { source: string; medium: string; name: string; content: string }

/** Короткие имена сетей → как их зовёт Google Analytics; незнакомое имя идёт как есть (любая сетка). */
const SOURCES: Record<string, string> = { tg: 'telegram_ads', fb: 'facebook', ig: 'instagram', gg: 'google', yt: 'youtube', tt: 'tiktok', x: 'twitter', inf: 'influencer', ch: 'telegram_channel' };

/** Метка «<сеть>-<кампания>[-<объявление>]»: «fb-launch1-v2» → facebook / paid / launch1 / v2. Кнопка бота —
 *  «bot-<тип уведомления>»: «bot-prediction_settled» → bot / notification / prediction_settled. Без метки — источник
 *  внутри Telegram по виду ссылки (приглашение друга — referral, матч из группы — share, прочее — organic). */
export function campaignOf(label: string | null, dest: string | undefined): Campaign {
  if (label) {
    const bot = /^bot-(.+)$/i.exec(label);
    if (bot) return { source: 'bot', medium: 'notification', name: clean(bot[1]!).slice(0, 100) || 'unnamed', content: '' };
    const [src = '', name = '', ...rest] = label.split('-');
    const source = SOURCES[src.toLowerCase()] ?? (clean(src) || 'other');
    return { source, medium: 'paid', name: clean(name) || 'unnamed', content: clean(rest.join('_')) };
  }
  const kind = launchSource(dest);
  return { source: 'telegram', medium: kind === 'ref' ? 'referral' : kind === 'direct' ? 'organic' : 'share', name: kind, content: '' };
}

/** Адрес «страницы» с метками utm — по ним Google Analytics сам раскладывает сессии и первых пользователей по кампаниям. */
export function campaignQuery(c: Campaign): string {
  const q = new URLSearchParams({ utm_source: c.source, utm_medium: c.medium, utm_campaign: c.name });
  if (c.content) q.set('utm_content', c.content);
  return q.toString();
}

// ---- Вехи: «первый раз» в жизни игрока — событие m_<веха> с возрастом (минуты с первого запуска, номер сессии) ----
/** Действие (имя a_*, успешное) → веха. Порядок важен: первое совпадение. first_favorite и first_prediction — не отсюда:
 *  их ставят события словаря favorite_add и prediction_submit {result: ok} (analytics.ts). */
const MILESTONES: Array<[RegExp, string]> = [
  [/^a_world_guilds.*join/, 'guild_join'],
  [/^a_world_battle_relics/, 'first_relic'],
  [/^a_world_battle_season/, 'first_season'],
  [/^a_world_battle_team_.*(rarity|star|rank)/, 'first_rarity_or_star'],
  [/^a_world_battle_team/, 'first_upgrade'],
  [/^a_world_battle_camp/, 'first_camp'],
  [/^a_world_battle_daily/, 'first_daily_boss'],
  [/^a_world_battle_skills?/, 'first_skill'],
  [/^a_world_store_(?!.*invoice)/, 'first_store_buy'],
  [/^a_world_tasks/, 'first_task'],
  [/^a_world_chests/, 'first_chest'],
  [/^a_world_login/, 'first_login_reward'],
  [/^a_world_friends|^a_world_referral/, 'first_friend_action'],
];
export function milestoneOf(action: string): string | null {
  for (const [re, name] of MILESTONES) if (re.test(action)) return name;
  return null;
}
/** Пороги уровня боя — вехи m_level_<N>: пройденные между прошлым и новым уровнем. */
export const LEVEL_MARKS = [2, 5, 10, 25, 50, 100, 150, 250, 500, 1000];
export const levelMarks = (from: number, to: number) => LEVEL_MARKS.filter((m) => m > from && m <= to);

// Обучение мира (HQ-190, словарь бэкенда 5.6.1): tutorial_begin {version, variant}, tutorial_step {key, n, kind},
// tutorial_complete {sec}, tutorial_skip {at_step}, tutorial_tip {key}, tutorial_replay — шлют useTutorial и «Как играть»
// (screens/world/tutorial); контекст screen, lvl, age_d, sess_n добавляет приёмник analytics.ts, как ко всем событиям.

// ---- Действия человека по словарю v1 (M2) ----
/** search_term: как набрал человек — обрезан по краям, нижний регистр, не длиннее 100 знаков. */
export const searchTerm = (text: string) => text.trim().toLowerCase().slice(0, 100);

/** Итог прогноза для prediction_submit: ok; prediction_closed (409 — матч начался); машинная причина отказа; сеть — network. */
export function predictionResult(error: { kind?: string; status?: number; code?: string | null; payload?: Record<string, unknown> | null } | null): string {
  if (!error) return 'ok';
  if (error.kind === 'network') return 'network';
  if (error.code === 'prediction_closed') return 'prediction_closed';
  return actionResult(error.status ?? 0, error.code ?? null, error.payload?.reason);
}

/** Как идёт ролик (video_play.delivery): поле сервера playback.delivery, иначе по адресу плеера — youtube / other. */
export function videoDelivery(playback: { provider?: string; delivery?: string; external_url?: string; embed_url?: string | null }): string {
  // «unknown» сервера (highlights.py: playback_delivery or "unknown") — не ответ: решаем по provider и адресу.
  const delivery = clean(playback.delivery ?? '').slice(0, 40);
  if (delivery && delivery !== 'unknown') return delivery;
  const url = `${playback.embed_url ?? ''} ${playback.external_url ?? ''}`;
  if (/youtube\.com|youtu\.be|youtube-nocookie\.com/i.test(url) || /youtube/i.test(playback.provider ?? '')) return 'youtube';
  return 'other';
}

// ---- Карточки спонсоров (sponsored_view / sponsored_click) ----
export type SponsoredPlacement = 'news_hub' | 'news_feed' | 'matches';
/** Номер карточки строкой: card_id сервера (C3), до него — из id «ad:<pk>»; не распознался — null (событие не шлём). */
export function sponsoredCardId(ad: { id: string; card_id?: number }): string | null {
  if (Number.isInteger(ad.card_id) && ad.card_id! > 0) return String(ad.card_id);
  return /^ad:(\d{1,12})$/.exec(ad.id)?.[1] ?? null;
}
/** Место карточки: placement сервера (C3), до него — место по экрану, где карточка нарисована. */
export const sponsoredPlacement = (ad: { placement?: string }, screen: SponsoredPlacement): string => ad.placement || screen;
